import { client } from 'graphql/client';
import { getStore } from 'app/hermes-redux';
import { isFeatureEnabled } from 'stores/features-list-store';
import { restrictions } from 'state/features-list/constants';
import { groupTimeslotsByStartDate } from 'pages/booking-shared/helpers/timeslot-helpers';

import {
  AppointmentStatus,
  AppointmentType,
  AssessedAsSuicideRiskDocument,
  AssessedAsSuicideRiskQuery,
  CounsellorProfileFragment,
  FetchUpcomingAppointmentsDocument,
  FetchUpcomingAppointmentsQuery,
  FetchYourCounsellorDocument,
  FetchYourCounsellorQuery,
  TimeSlotFragment,
} from 'graphql-types';
import type { SortedDatesArray } from '../containers/timeslot-selection/state/hooks';

import { hasCarePlanAssigned } from '../../care-plan/care-plan.helpers';
import { MyCareRoutes } from '../../my-care/my-care.constants';
import { showToast } from 'toaster-comp/state/actions';
import { ToastType } from 'toaster-comp/state/types';

// Booking UI helpers now live in booking-shared; re-exported so TMH callers
// (and their test mocks of this module) keep working.
export {
  getCounsellorName,
  getCounsellorShortName,
} from 'pages/booking-shared/helpers/counsellor-helpers';
export {
  formatAppointmentTime,
  getAdjacentAvailableDates,
} from 'pages/booking-shared/helpers/timeslot-helpers';

export const getJoinedJobTitles = (
  jobTitles?: CounsellorProfileFragment['jobTitles'] | string[],
): string => jobTitles?.filter(Boolean).join(', ') ?? '';

export const groupByStartDate = (
  timeslots: Array<TimeSlotFragment>,
): SortedDatesArray =>
  groupTimeslotsByStartDate(timeslots).map(({ date, timeslots }) => ({
    date,
    firstAvailableTimeslot:
      timeslots.morning[0] ||
      timeslots.afternoon[0] ||
      timeslots.evening[0] ||
      null,
    timeslots,
  }));

export const getHasAssignedCounsellor = async () => {
  const { data: counsellorData } = await client.query<FetchYourCounsellorQuery>(
    {
      query: FetchYourCounsellorDocument,
    },
  );

  return {
    hasAssignedCounsellor: !!counsellorData?.fetchCounsellor?.counsellor,
    appointmentTypes:
      counsellorData?.fetchCounsellor?.counsellor?.appointmentTypes,
    isBookable: counsellorData?.fetchCounsellor?.counsellor?.allowsBooking,
    canViewHistory: counsellorData?.fetchCounsellor?.counsellor?.displayHistory,
  };
};

export const getHasUpcomingAppointments = async () => {
  const { data: appointmentsData } =
    await client.query<FetchUpcomingAppointmentsQuery>({
      query: FetchUpcomingAppointmentsDocument,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          first: 1,
          where: {
            statuses: [AppointmentStatus.Pending, AppointmentStatus.Booked],
          },
        },
      },
    });

  // in the BETA a user should only ever be able to book 1 appointment - this will eventually change to 3 or more.
  return (
    (appointmentsData?.fetchAppointments?.appointments?.data?.length ?? 0) >= 1
  );
};

export const getUserIsAtRisk = async () => {
  const { data } = await client.query<AssessedAsSuicideRiskQuery>({
    query: AssessedAsSuicideRiskDocument,
  });

  return {
    userIsAtRisk: !!data?.assessedAsSuicideRisk?.ccsTeamTelephoneNumber,
  };
};

export const showRedirectionToast = (message: string, type = ToastType.INFO) =>
  getStore().dispatch(
    showToast({
      message,
      type,
    }),
  );

export const shouldGrantAccessToCounsellorSelectionFlow = async () => {
  if (!hasAppointmentsAccess()) {
    return { provideAccess: false, redirectTo: 'pageNotFound' };
  }

  try {
    const hasCarePlan = await hasCarePlanAssigned();
    const { hasAssignedCounsellor, appointmentTypes } =
      await getHasAssignedCounsellor();
    const hasUpcomingAppointments = await getHasUpcomingAppointments();
    const { userIsAtRisk } = await getUserIsAtRisk();

    if (!hasCarePlan) {
      // if the user has no care plan assigned and tries to access any part of appointments
      // they should be redirected to the care hub.

      showRedirectionToast(
        polyglot.t('appointments.toaster.choose_a_careplan'),
      );

      return {
        provideAccess: false,
        redirectTo: MyCareRoutes.MY_CARE_HUB,
      };
    } else if (hasAssignedCounsellor) {
      // if the user has an assigned counsellor while trying to access the counsellor selection
      // we need to check if selected counsellor can be changed to a new one

      const isInPersonCounsellor = appointmentTypes?.includes(
        AppointmentType.InPerson,
      );

      if (userIsAtRisk || isInPersonCounsellor || hasUpcomingAppointments) {
        return {
          provideAccess: false,
          redirectTo: MyCareRoutes.MY_CARE_HUB,
        };
      }
    }

    return {
      provideAccess: true,
      redirectTo: '',
    };
  } catch {
    // if any error is caught send them to the MyCare Hub

    showRedirectionToast(
      polyglot.t('api_errors.something_wrong'),
      ToastType.ERROR,
    );

    return {
      provideAccess: false,
      redirectTo: MyCareRoutes.MY_CARE_HUB,
    };
  }
};

export const shouldGrantAccessToViewAppointments = async () => {
  if (!hasAppointmentsAccess()) {
    return { provideAccess: false, redirectTo: 'pageNotFound' };
  }

  try {
    const { appointmentTypes, canViewHistory } =
      await getHasAssignedCounsellor();

    if (
      appointmentTypes?.includes(AppointmentType.InPerson) ||
      !canViewHistory
    ) {
      // if the user is trying to view their appointments list we want to redirect
      // them to the My Care Hub because they can only book appointments through the chat
      // and its not guaranteed that any other appointments they book with their counsellor
      // in person are returned via Dynamics or Solace

      showRedirectionToast(
        polyglot.t('appointments.toaster.in_person_counsellor'),
      );

      return {
        provideAccess: false,
        redirectTo: MyCareRoutes.MY_CARE_HUB,
      };
    }

    return {
      provideAccess: true,
      redirectTo: '',
    };
  } catch {
    // if any error is caught send them to the MyCare Hub

    showRedirectionToast(
      polyglot.t('api_errors.something_wrong'),
      ToastType.ERROR,
    );

    return {
      provideAccess: false,
      redirectTo: MyCareRoutes.MY_CARE_HUB,
    };
  }
};

/**
 * Gate for every route in the appointments module: legacy TMH access requires both
 * counsellor flags together; WPO access is a separate, independent path. The WPO
 * booking flow now lives in its own `wpo-appointments` module, but the manage
 * screens (view/cancel/details) are still shared, so WPO users retain access here.
 */
export const hasAppointmentsAccess = (): boolean =>
  (isFeatureEnabled(restrictions.counsellor_access) &&
    isFeatureEnabled(restrictions.counsellor_appointments)) ||
  isFeatureEnabled(restrictions.wpo_online_scheduling);

export const shouldGrantAccessBasedOnAppointmentsAccess = async () =>
  hasAppointmentsAccess()
    ? { provideAccess: true, redirectTo: '' }
    : { provideAccess: false, redirectTo: 'pageNotFound' };

export const shouldGrantAccessToAppointmentsSelectionFlow = async () => {
  if (!hasAppointmentsAccess()) {
    return { provideAccess: false, redirectTo: 'pageNotFound' };
  }

  try {
    const hasCarePlan = await hasCarePlanAssigned();
    const { hasAssignedCounsellor, appointmentTypes, isBookable } =
      await getHasAssignedCounsellor();

    const hasUpcomingAppointments = await getHasUpcomingAppointments();
    const isInPersonCounsellor = appointmentTypes?.includes(
      AppointmentType.InPerson,
    );

    if (!hasCarePlan) {
      // if the user has no care plan assigned and tries to access any part of appointments
      // they should be redirected to the care hub.

      showRedirectionToast(
        polyglot.t('appointments.toaster.choose_a_careplan'),
      );

      return {
        provideAccess: false,
        redirectTo: MyCareRoutes.MY_CARE_HUB,
      };
    } else if (hasAssignedCounsellor) {
      if (isInPersonCounsellor || !isBookable) {
        // if the user has an assigned counsellor while trying to access the book appointment flow
        // and the counsellor has IN_PERSON appointment types we should send them back to the hub
        // and have them book utilize the chat instead

        showRedirectionToast(
          polyglot.t('appointments.toaster.in_person_counsellor'),
        );

        return {
          provideAccess: false,
          redirectTo: MyCareRoutes.MY_CARE_HUB,
        };
      } else if (hasUpcomingAppointments) {
        // if the user has an assigned counsellor and upcoming appointments we should send
        // them back to My Care Hub as currently they cannot book more than 1 appointment
        // through the Web App

        showRedirectionToast(
          polyglot.t('appointments.toaster.already_have_appointment'),
        );

        return {
          provideAccess: false,
          redirectTo: MyCareRoutes.MY_CARE_HUB,
        };
      }

      return {
        provideAccess: true,
        redirectTo: '',
      };
    }

    return {
      provideAccess: true,
      redirectTo: '',
    };
  } catch {
    // if any error is caught send them to the MyCare Hub

    showRedirectionToast(
      polyglot.t('api_errors.something_wrong'),
      ToastType.ERROR,
    );

    return {
      provideAccess: false,
      redirectTo: MyCareRoutes.MY_CARE_HUB,
    };
  }
};
